import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { Box, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useQueryClient } from "@tanstack/react-query";
import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../components/InHouseTooltip/InHouseTooltip";
import { JumpingDots } from "../../../components/UIToolkit/Animations/JumpingDots";
import { MetaChip } from "../../../components/UIToolkit/Badges/MetaChip";
import { TooltipTextCell } from "../../../components/UIToolkit/Cells/TooltipTextCell/TooltipTextCell";
import { EmptyState } from "../../../components/UIToolkit/molecules/EmptyState";
import { SystemBanner } from "../../../components/UIToolkit/molecules/SystemBanner";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import type { SystemGridColDef } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import { SystemDataGrid } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { FONT_SIZE, INK } from "../../../theme/tokens";
import type { MultiSortOption, SortingState } from "../../../utils/multiColumnSorting";
import {
	type ActorNames,
	actorDisplay,
	actorSearchText,
	BULK_UPDATE_ACTION_NAME,
	CHANGE_TYPE_LABEL_KEY,
	CHANGE_TYPE_TONE,
	compareActors,
	composeRowWhatText,
	formatActivityLogWhen,
	formatRecurringRecurrenceLabel,
	formatRecurringShiftTimeRange,
	formatRequestDetailSublabel,
	formatShiftDetailRange,
	GROUP_DETAIL_GRID_HEIGHT,
	OBJECT_TYPE_LABEL_KEY,
	PTO_DATE_RANGE_TABLE_NAME,
	PTO_HOURS_TABLE_NAME,
	PUNCH_CHANGE_TYPE,
	PUNCH_TABLE_NAME,
	ptoDateRangeFromChanges,
	ptoDurationFromChanges,
	requestStatusChip,
} from "./activityLogGroupedDisplay";
import { GroupedActivityLogRowMenu } from "./GroupedActivityLogRowMenu";
import { type ActivityLogGroupRow, activityLogGroupDetailQueryKey, useActivityLogGroupDetailQuery } from "./useActivityLogGroupDetailQuery";
import type { ResolvedGroupedActivityLogEntry } from "./useGroupedActivityLogQuery";

/** The table whose rows carry a shift's own `start_time`/`end_time` in `changes` — drives the detail
 * grid's "Details" column (see `changes` shape note on `ActivityLogGroupRow`). Deliberately narrower
 * than the outer grid's own Call-Off/Shift `details.kind` check: `nurse_in_shift_actual` (punch) rows
 * share the same `changes` shape but are excluded per this column's spec — every other table renders
 * just its reason (or "—" if it has none). */
const SHIFT_DETAIL_TABLE_NAME = "nurse_in_shift";

/** `recurring_shift`'s own `changes` snapshot carries its shift's start/end the same raw way
 * `nurse_in_shift` does (see `SHIFT_DETAIL_TABLE_NAME` above) — a distinct table constant since it's a
 * separate business object (`Recurring Shift`, not `Nurse Shift`; see `OBJECT_TYPE_LABEL_KEY`). */
const RECURRING_SHIFT_TABLE_NAME = "recurring_shift";

/** The `requests_status` table's own `changes` carries no job/shift-date context of its own — the
 * backend joins that back onto the row's `details` field instead (see `ActivityLogGroupRow.details`'s doc
 * comment). The `requests` table (Open-Shift-Posted) carries the same shape raw on `changes`. */
const REQUESTS_STATUS_TABLE_NAME = "requests_status";
const REQUESTS_TABLE_NAME = "requests";

/**
 * Marks this panel's own root so the "go to transaction" icon's `.MuiDataGrid-row:hover &` sx rule can
 * be scoped to THIS nested grid's rows only. Without this, the rule (a plain CSS descendant selector)
 * also matches the OUTER grid's own synthetic detail row — which wraps this whole panel and is itself a
 * `.MuiDataGrid-row` — and CSS `:hover` bubbles up to it from anywhere inside, so every icon in every
 * nested row would light up together instead of just the one under the cursor.
 */
const DETAIL_PANEL_ROOT_CLASS = "activity-log-group-detail-panel-root";

/** Column `field`s gated behind Dev Mode — Transaction (own/linked lineage) and its trailing copy-menu
 * column (meaningless without Transaction visible), Table (raw table name), Changes (raw key/value
 * dump), and What (the business-object classification) — confirmed deliberate: Dev Mode OFF shows just
 * When/Details/Change Type/Changed By. Also drives which `sortingOptions` entries are offered, so the
 * sort popup never lists a column that isn't on screen. */
const DEV_MODE_ONLY_FIELDS: ReadonlySet<keyof ActivityLogGroupRow | "options"> = new Set(["objectType", "apiRequestId", "tableName", "changes", "options"]);

/**
 * Lifecycle of the uncapped (`full=true`) read for one open panel. `idle` — not started. `fetching` —
 * armed automatically once the capped read comes back truncated; expanding the row is the consent, so
 * there is no prompt. `stopped` — cancelled; capped rows stay and the banner becomes a manual retry.
 * `stopped` is terminal on purpose, or the auto-start effect would re-arm the fetch it just cancelled.
 * A fetch that fails stays at `fetching`; the query's own `isError` drives the same retry banner.
 */
type FullFetchState = "idle" | "fetching" | "stopped";

type Props = {
	// Only ever rendered for a `kind: "resolved"` + `isExpandable` row (see `GroupedActivityLog.tsx`'s
	// `getRowExpandContent`) — a still-deferred row is never expandable, so this panel never sees one.
	entry: ResolvedGroupedActivityLogEntry;
	/** Writes an `api_request_id` into the OUTER grid's own search box — wired to the Transaction
	 * column's hover "go to transaction" action below, so a user can pivot from a row here to that
	 * transaction's own top-level group (subject to the outer grid's current date range/filters). */
	onGoToTransaction: (apiRequestId: string) => void;
	/** Mirrors the outer grid's own Dev Mode toggle (`GroupedActivityLog.tsx`) — gates this grid's
	 * investigative Transaction/Table/Changes/What columns (and the trailing lineage-menu column, which
	 * is meaningless without Transaction visible). Off by default: When/Details/Change Type/Changed By
	 * only. */
	devMode: boolean;
	/** Mirrors the outer grid's own Punches toggle — threaded into this row's OWN detail fetch
	 * (`useActivityLogGroupDetailQuery`) so a punch row can be included/excluded here the same way it is
	 * in the outer grid, with no client-side filtering. */
	includePunches: boolean;
	/** Did the user already stop this group's uncapped "load the rest" fetch? Owned by
	 * `GroupedActivityLog.tsx` and keyed by group, because this panel unmounts on collapse — panel-local
	 * state would reset to `idle` on the next expand and silently restart the expensive request the user
	 * just cancelled. Seeds `fullFetchState`, so a reopened panel stays on the manual `Retry` affordance. */
	isRemainingFetchStopped: boolean;
	/** Reports a stop (true) or a manual retry (false) back to that owner. */
	onRemainingFetchStoppedChange: (apiRequestId: string, stopped: boolean) => void;
};

/** Renders arbitrary tier-specific JSON as a compact, readable `key: value · key: value` line. */
const formatChangesCompact = (changes: Record<string, unknown> | undefined | null): string => {
	const entries = Object.entries(changes ?? {});
	if (entries.length === 0) return "—";
	return entries
		.map(([key, value]) => `${key}: ${value === null || value === undefined ? "—" : typeof value === "object" ? JSON.stringify(value) : String(value)}`)
		.join(" · ");
};

/**
 * One expanded group's full detail: every raw row behind it. The first read is capped server-side at
 * `DETAIL_ROW_CAP`; when that comes back truncated the remaining rows are fetched automatically, with
 * an in-panel progress banner and a Cancel (see `FullFetchState`). Mounts only when its row is
 * actually expanded (see
 * `GroupedActivityLog.tsx`'s `getRowExpandContent`), so the mount effect below fires exactly once per
 * real user expand — the right place to fire the `row-expand` analytics event.
 *
 * Each expanded row gets its own instance of this component (and its own
 * `useActivityLogGroupDetailQuery`), keyed by its own `apiRequestId` — concurrent expansion of several
 * rows never shares or interferes with state.
 */
export const GroupDetailPanel: FC<Props> = ({ entry, onGoToTransaction, devMode, includePunches, isRemainingFetchStopped, onRemainingFetchStoppedChange }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { chosenDepartment, timezone, getNurseFormattedName } = useHospitalData();
	const queryClient = useQueryClient();
	// Lazy initializer, deliberately: the prop only seeds the FIRST render of each mount. After that this
	// panel owns the state for as long as it is open, so a retry here isn't undone by a re-render.
	const [fullFetchState, setFullFetchState] = useState<FullFetchState>(() => (isRemainingFetchStopped ? "stopped" : "idle"));

	// Two instances of the same query, one per `full` value — that dimension is part of the cache key
	// (see the hook's doc comment), which is exactly what lets both live at once. The capped read is
	// always on (and for a just-resolved deferred group is already seeded by `/resolve`'s embedded detail,
	// so opening that row costs no second request — no spinner inside a panel that just finished loading);
	// the uncapped one
	// is armed only while its automatic fetch is running.
	const cappedQuery = useActivityLogGroupDetailQuery(entry.apiRequestId, false, includePunches, true);
	const fullQuery = useActivityLogGroupDetailQuery(entry.apiRequestId, true, includePunches, fullFetchState === "fetching");

	// The full set replaces the capped one the moment it lands, and until then the capped rows are what
	// renders. This is a precedence choice, not a fallback for missing data: the user must never lose
	// rows they can already see (or get a loading skeleton in their place) just because a second,
	// larger read is in flight behind them.
	const detail = fullQuery.data ?? cappedQuery.data;
	// Both loading/error states belong to the CAPPED read alone — it is the one that decides whether the
	// grid has anything to show. The uncapped read's own progress and failure are reported by the banner
	// above the grid instead, so a slow or failed "load the rest" never blanks out the rows on screen.
	const { isLoading, isError } = cappedQuery;

	// The only trigger for the automatic fetch, per spec — it is `rows.length < totalCount` and nothing
	// else, so this covers any truncated group, not just a resolved-deferred one.
	const isTruncated = !!cappedQuery.data && cappedQuery.data.rows.length < cappedQuery.data.totalCount;

	// Open-Shift-Posted (`requests` Insert) detail rows carry only `job_id` on their raw `changes` — no
	// display name (see `REQUESTS_TABLE_NAME`'s doc comment). This department's own job list is already
	// available (no fresh backend lookup needed) via `useHospitalData`, same "check for an existing
	// client-side map before adding an endpoint" convention as the rest of this codebase.
	const jobNameById = useMemo(() => new Map((chosenDepartment.jobs ?? []).map((job) => [job.id, job.displayName])), [chosenDepartment.jobs]);

	// Fires once, on the real mount that follows the user opening this row (see the docstring above).
	// biome-ignore lint/correctness/useExhaustiveDependencies: deliberately mount-only — a fresh `entry`/`track` on re-render must not re-fire this.
	useEffect(() => {
		track("activity_log_group_expanded", {
			department_id: chosenDepartment?.department_id,
			api_request_id: entry.apiRequestId,
			action_name: entry.actionName,
			group_size: entry.groupSize,
		});
	}, []);

	// Arms the uncapped fetch as soon as the capped read admits it was truncated. Tied to this panel's
	// lifetime by construction: the panel mounts and unmounts with the row's expansion (see the
	// docstring above), and `fullFetchState` is panel-local, so an unexpanded row never fetches and a
	// collapsed one has its in-flight request aborted by query-core (the `queryFn` consumes `signal`,
	// so the last observer leaving cancels the fetch — see the hook's doc comment).
	//
	// No render loop: the ONLY state this writes is the same state its own guard reads, so the second
	// pass returns before the `setFullFetchState`. `displayedEntries`-style derived data isn't involved
	// — `isTruncated` comes from server data, never from this state.
	useEffect(() => {
		if (fullFetchState !== "idle" || !isTruncated) return;
		setFullFetchState("fetching");
		track("activity_log_group_detail_remaining_autofetch_start", {
			department_id: chosenDepartment?.department_id,
			api_request_id: entry.apiRequestId,
			shown_count: cappedQuery.data?.rows.length,
			total_count: cappedQuery.data?.totalCount,
		});
	}, [fullFetchState, isTruncated, cappedQuery.data, track, chosenDepartment?.department_id, entry.apiRequestId]);

	// 12-hour `h:mm a` (2026-09-07 fix, was 24-hour `HH:mm`) — the audit timestamp (when this log entry was
	// WRITTEN) is a different semantic than a shift's own time, but the app has one time-of-day convention
	// throughout, matching `ShiftHoursDisplayAndPicker.tsx`'s existing `.toFormat("h:mm a")`.
	const formatWhen = (value: string): string => formatActivityLogWhen(value, timezone);
	// Plain generic mapping — always "Created"/"Updated"/"Deleted", regardless of table. The
	// `nurse_in_shift_actual` Insert (punch) case is called out separately via the "Punched" badge
	// rendered alongside this label in the Change Type column below, not by overriding the label itself.
	const changeTypeLabel = (changeType: string): string => t(CHANGE_TYPE_LABEL_KEY[changeType] ?? changeType);
	// The Change Type cell's full text — base label plus the request-status outcome chip, when there is
	// one — so the column's sort and quick search see the same "Updated Approved" the cell shows.
	const changeTypeText = (row: ActivityLogGroupRow): string => {
		const statusChip = requestStatusChip(row);
		return statusChip ? `${changeTypeLabel(row.changeType)} ${t(statusChip.labelKey)}` : changeTypeLabel(row.changeType);
	};
	// "Changed By" render, sort and search all read the actor through this, so they can't disagree.
	const rowActor = (row: ActivityLogGroupRow): ActorNames => ({ firstName: row.changedUserFirstName, username: row.changedUser });
	const isPunchRow = (row: ActivityLogGroupRow): boolean => row.tableName === PUNCH_TABLE_NAME && row.changeType === PUNCH_CHANGE_TYPE;

	// A row belongs to the group's OWN `api_request_id` transaction, or was pulled in via the
	// requests/requests_status lineage chain from a DIFFERENT transaction (see `ActivityLogGroupRow`'s
	// doc comment on `apiRequestId`). Drives the "Transaction" column + the trailing copy-menu column.
	const isOwnTransaction = (row: ActivityLogGroupRow): boolean => row.apiRequestId === entry.apiRequestId;
	const transactionLabel = (row: ActivityLogGroupRow): string =>
		t(isOwnTransaction(row) ? "activityLog.grouped.detail.transaction.own" : "activityLog.grouped.detail.transaction.linked");

	// "What" — which business object this row is about (backend-computed onto `objectType`, see
	// `OBJECT_TYPE_LABEL_KEY`'s doc comment), not its raw reason: a shift row's `reason` is either an
	// internal tombstone message or a call-off reason already shown as the group's own headline `what` in
	// the outer grid — neither belongs here. Reused below by `detailsText` too (2026-09-06) — Dev Mode
	// hides this "What" column by default, so "Details" now ALWAYS leads with the same translated label.
	const objectTypeLabel = (objectType: string): string => t(OBJECT_TYPE_LABEL_KEY[objectType] ?? objectType);

	// Per-table dispatch for the "Details" column's row-specific context — mirrors the outer grid's own
	// per-tier "What" sublabel dispatch (`GroupedActivityLog.tsx`'s `whatSublabel`), but keyed by
	// `tableName` since a detail row is a single raw table write, not a tier's ranked representative.
	const rowDetailSublabel = (row: ActivityLogGroupRow): string | null => {
		// Raw passthrough dict (see `ActivityLogGroupRow.changes`) — the cast follows the same
		// documented-contract convention already used for `details` in `GroupedActivityLog.tsx`.
		if (row.tableName === SHIFT_DETAIL_TABLE_NAME) {
			const changes = row.changes as { start_time?: string | null; end_time?: string | null } | null;
			return formatShiftDetailRange(changes?.start_time ?? null, changes?.end_time ?? null, timezone);
		}
		if (row.tableName === RECURRING_SHIFT_TABLE_NAME) {
			// `shift_start`/`shift_end` here are bare time-of-day strings (Postgres `Time`, no date, no
			// zone) — NOT the ISO datetimes `formatShiftDetailRange` expects. See
			// `formatRecurringShiftTimeRange`'s doc comment for why the two can't share a formatter.
			const changes = row.changes as {
				shift_start?: string | null;
				shift_end?: string | null;
				location?: string | null;
				recurring_params?: Record<string, unknown> | null;
			} | null;
			const timeRange = formatRecurringShiftTimeRange(changes?.shift_start ?? null, changes?.shift_end ?? null, timezone);
			// Recurrence type + weekday(s) (2026-09-06) — e.g. "Weekly on Tuesday" — plus location, so a
			// row's Details reads as the actual recurring pattern, not just its time-of-day range. `t`
			// passed through (this file's own already-in-scope translation function) — the formatter
			// itself stays a plain, translation-agnostic function per this codebase's "caller owns
			// translation" convention.
			const recurrenceLabel = formatRecurringRecurrenceLabel(changes?.recurring_params, t);
			return [timeRange, recurrenceLabel, changes?.location].filter(Boolean).join(" · ") || null;
		}
		if (row.tableName === PTO_HOURS_TABLE_NAME || row.tableName === PTO_DATE_RANGE_TABLE_NAME) {
			const dateRange = ptoDateRangeFromChanges(row.changes);
			const duration = ptoDurationFromChanges(row.tableName, row.changes);
			const durationLabel = duration
				? "hours" in duration
					? t("activityLog.grouped.detail.pto.hours", { count: duration.hours })
					: t("activityLog.grouped.detail.pto.days", { count: duration.days })
				: null;
			return [dateRange, durationLabel].filter(Boolean).join(" · ") || null;
		}
		// `requests_status` carries no job/shift-date context of its own — the backend's per-row `details`
		// join is the only source (see `ActivityLogGroupRow.details`'s doc comment); never derived from
		// `changes` here.
		if (row.tableName === REQUESTS_STATUS_TABLE_NAME) {
			if (!row.details) return null;
			// `row.details` is already typed as `RequestDetailFields` (2026-09-06 fix) — no cast needed.
			const neededLabel = row.details.needed_nurses != null ? t("activityLog.grouped.what.neededNurses", { count: row.details.needed_nurses }) : null;
			return formatRequestDetailSublabel(row.details, timezone, neededLabel);
		}
		// `requests` (Open-Shift-Posted) carries the same shape raw on `changes` — only the job DISPLAY
		// name is missing (`job_id` alone isn't user-facing), resolved via this department's own job list.
		if (row.tableName === REQUESTS_TABLE_NAME) {
			const changes = row.changes as {
				shift_start?: string | null;
				shift_end?: string | null;
				location?: string | null;
				needed_nurses?: number | null;
				job_id?: string | null;
			} | null;
			if (!changes) return null;
			const job = changes.job_id ? (jobNameById.get(changes.job_id) ?? null) : null;
			const neededLabel = changes.needed_nurses != null ? t("activityLog.grouped.what.neededNurses", { count: changes.needed_nurses }) : null;
			return formatRequestDetailSublabel({ ...changes, job }, timezone, neededLabel);
		}
		return null;
	};

	// Real composed business SENTENCES (2026-09-07), keyed by `row.rowWhat` — extracted into
	// `composeRowWhatText` (`activityLogGroupedDisplay.ts`) as a pure, standalone function so this
	// composition is directly unit-testable without mounting the component (see that function's own doc
	// comment for the full vocabulary/composition styles this dispatches across).
	const rowWhatText = (row: ActivityLogGroupRow): string | null => composeRowWhatText(row, timezone, t, jobNameById);

	// "Details" — a real composed sentence (`rowWhatText`, 2026-09-07) when this row's own `rowWhat` is
	// covered; otherwise the pre-existing composition, which ALWAYS leads with this row's own translated
	// `objectType` label (2026-09-06 fix: Dev Mode hides the "What"/`objectType` column by default, so
	// without this prefix there was no visible indication of which business object a row is — e.g. a Call
	// Off group's nested `nurse_in_shift` row read as just "Appointment · Sep 3, 2026, 6:00 AM–6:30 PM",
	// giving no hint it's actually a Nurse Shift row, not a Call Off-table entry). Followed by the row's
	// own reason (unmodified — omitted entirely when suppressed/absent, e.g. a tombstoned shift-deletion
	// reason or a generic per-table trigger literal; see `_suppress_default_reason`'s doc comment on the
	// backend, `database/activity_log_grouping.py`) and, for the tables above, row-specific context (a
	// shift's date/time range, a PTO span/duration, or a request's job/shift-date).
	const detailsText = (row: ActivityLogGroupRow): string => {
		const sentence = rowWhatText(row);
		if (sentence) return sentence;
		const parts = [objectTypeLabel(row.objectType), row.reason, rowDetailSublabel(row)].filter((part): part is string => !!part);
		return parts.length > 0 ? parts.join(" · ") : "—";
	};

	// One axis per column. `changeType`, `changes`, and `transaction` sort by the same resolved text the
	// cell actually shows (the translated chip label; the compact key/value line; the own/linked label)
	// rather than the raw enum/JSON/boolean — everything else sorts its raw field directly. No
	// `localStorageKey`: each expanded row mounts its own grid instance, so persisting sort per-instance
	// would be meaningless.
	//
	// "Details" (`reason` + the shift range, combined) has no sortingOptions entry: `sortColumns`
	// requires a real `keyof ActivityLogGroupRow`, and "Details" is a composite of two fields with no
	// single one to sort by. It's marked `sortable: false` instead, the same convention the trailing
	// `options` column already uses for a non-data column. "What" CAN sort now — `objectType` is its own
	// real backend field, distinct from `tableName`, so it doesn't hit the Table column's sortColumn
	// collision an earlier, client-derived version of this column had.
	const allSortingOptions: MultiSortOption<ActivityLogGroupRow>[] = [
		{ label: t("activityLog.grouped.detail.column.when"), sortColumns: ["changedAt"] },
		{
			label: t("activityLog.grouped.detail.column.transaction"),
			sortColumns: ["apiRequestId"],
			customSortFunction: (a, b): 1 | 0 | -1 => {
				const cmp = transactionLabel(a).localeCompare(transactionLabel(b));
				return cmp === 0 ? 0 : cmp > 0 ? 1 : -1;
			},
		},
		{ label: t("activityLog.grouped.detail.column.table"), sortColumns: ["tableName"] },
		{
			label: t("activityLog.grouped.detail.column.changedUser"),
			sortColumns: ["changedUser"],
			sortNullUndefinedLast: true,
			customSortFunction: (a, b) => compareActors(rowActor(a), rowActor(b)),
		},
		// Reuses the outer grid's own "Performed For" translation verbatim (2026-09-06 fix) — same
		// label, same meaning, just resolved per-row here instead of per-group.
		{ label: t("activityLog.grouped.column.performedFor"), sortColumns: ["performedFor"], sortNullUndefinedLast: true },
		{
			label: t("activityLog.grouped.detail.column.changeType"),
			sortColumns: ["changeType"],
			customSortFunction: (a, b): 1 | 0 | -1 => {
				const cmp = changeTypeText(a).localeCompare(changeTypeText(b));
				return cmp === 0 ? 0 : cmp > 0 ? 1 : -1;
			},
		},
		{
			label: t("activityLog.grouped.detail.column.what"),
			sortColumns: ["objectType"],
			customSortFunction: (a, b): 1 | 0 | -1 => {
				const cmp = objectTypeLabel(a.objectType).localeCompare(objectTypeLabel(b.objectType));
				return cmp === 0 ? 0 : cmp > 0 ? 1 : -1;
			},
		},
		{
			label: t("activityLog.grouped.detail.column.changes"),
			sortColumns: ["changes"],
			customSortFunction: (a, b): 1 | 0 | -1 => {
				const cmp = formatChangesCompact(a.changes).localeCompare(formatChangesCompact(b.changes));
				return cmp === 0 ? 0 : cmp > 0 ? 1 : -1;
			},
		},
	];
	// Dev-mode-only sort axes are dropped, not just their columns — otherwise the sort popup would still
	// offer "Table"/"Changes"/etc. sort options for columns that aren't on screen.
	const sortingOptions = allSortingOptions.filter((option) => devMode || !option.sortColumns?.some((column) => DEV_MODE_ONLY_FIELDS.has(column)));
	// Was implicitly ascending (the backend's own `ORDER BY changed_at, id`, shown as-is with no FE
	// sort applied) — flipped to descending so the most recent row in the group leads (2026-09-03 fix).
	const defaultSortState: SortingState<ActivityLogGroupRow>[] = [
		{ sortColumns: ["changedAt"], sortOrder: "desc", label: t("activityLog.grouped.detail.column.when") },
	];

	const allColumns: SystemGridColDef<ActivityLogGroupRow>[] = [
		{
			field: "changedAt",
			headerName: t("activityLog.grouped.detail.column.when"),
			flex: devMode ? 0.7 : 0.3,
			minWidth: 150,
			// MUI's plain-text vertical centering trick doesn't apply once Typography sets its own
			// line-height, so every custom-rendered cell needs an explicit centered wrapper (matches
			// `teamGridColumns.tsx`'s convention) to stay aligned across the row.
			renderCell: (params) => (
				<Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
					<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY }}>{formatWhen(params.row.changedAt)}</Typography>
				</Box>
			),
		},
		{
			field: "objectType",
			headerName: t("activityLog.grouped.detail.column.what"),
			flex: 0.7,
			minWidth: 140,
			renderCell: (params) => (
				<Box sx={{ display: "flex", alignItems: "center", height: "100%", minWidth: 0 }}>
					<Typography
						sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}
					>
						{objectTypeLabel(params.row.objectType)}
					</Typography>
				</Box>
			),
		},
		{
			field: "changeType",
			headerName: t("activityLog.grouped.detail.column.changeType"),
			// Wide enough for two chips side by side (base + "Punched" or a request-status outcome),
			// including the longer Spanish pair "Actualizado" + "Rechazado".
			flex: devMode ? 0.7 : 0.55,
			minWidth: 200,
			renderCell: (params) => {
				const statusChip = requestStatusChip(params.row);
				return (
					<Box sx={{ display: "flex", alignItems: "center", height: "100%", gap: 0.5 }}>
						<MetaChip
							label={changeTypeLabel(params.row.changeType)}
							tone={CHANGE_TYPE_TONE[params.row.changeType] ?? "neutral"}
							dataTestId={`activity-log-group-detail-change-type-${params.row.id}`}
						/>
						{/* Base label always stays generic ("Created"/"Updated") — a real punch (`nurse_in_shift_actual`
						Insert) and a request-status outcome each add a chip beside it rather than overriding it. */}
						{isPunchRow(params.row) && (
							<MetaChip label={t("activityLog.grouped.punchBadge")} tone="info" dataTestId={`activity-log-group-detail-punch-badge-${params.row.id}`} />
						)}
						{statusChip && (
							<MetaChip label={t(statusChip.labelKey)} tone={statusChip.tone} dataTestId={`activity-log-group-detail-status-chip-${params.row.id}`} />
						)}
					</Box>
				);
			},
		},
		{
			field: "details",
			headerName: t("activityLog.grouped.detail.column.details"),
			flex: 1.1,
			minWidth: 170,
			sortable: false,
			renderCell: (params) => (
				<Box sx={{ display: "flex", alignItems: "center", height: "100%", minWidth: 0 }}>
					<InHouseTooltip title={detailsText(params.row)}>
						<Typography
							sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}
						>
							{detailsText(params.row)}
						</Typography>
					</InHouseTooltip>
				</Box>
			),
		},
		{
			field: "changedUser",
			headerName: t("activityLog.grouped.detail.column.changedUser"),
			flex: 0.8,
			minWidth: 140,
			// Same rendering as the outer grid's Performed By cell: a first name, its email on hover.
			renderCell: (params) => {
				const { label, tooltip } = actorDisplay(rowActor(params.row));
				return <TooltipTextCell text={label} tooltip={tooltip} dataTestId={`activity-log-group-detail-changed-by-${params.row.id}`} />;
			},
		},
		{
			// Which nurse THIS row concerns (2026-09-06 fix) — previously the detail grid had no such
			// column at all, a real gap for every row and especially for a Bulk Update expand (dozens of
			// rows, each potentially a different nurse — see the outer grid's own analogous
			// `affectedNurseCount` fix). Mirrors the outer grid's own "Performed For" column exactly
			// (`GroupedActivityLog.tsx`) — same nurse-name lookup, same "go to profile" action, same
			// translation keys — just resolved per-row (`row.performedFor`) instead of per-group. ALWAYS
			// visible (not gated behind Dev Mode): this is core identifying info, not a debug field.
			field: "performedFor",
			headerName: t("activityLog.grouped.column.performedFor"),
			flex: 0.9,
			minWidth: 160,
			renderCell: (params) => (
				<Box sx={{ display: "flex", alignItems: "center", height: "100%", minWidth: 0, gap: 0.5 }}>
					<Typography
						sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}
					>
						{/* `performedForName` first, mirroring the outer grid's `composePerformedForLabel`: it is set only
						for a hard-deleted employee, whose `performedFor` is necessarily null, and it is already a
						display name — `getNurseFormattedName` cannot resolve a nurse that no longer exists. The
						profile link below stays correctly hidden for that row: a deleted nurse has no profile. */}
						{params.row.performedForName ||
							(params.row.performedFor !== null && params.row.performedFor !== undefined ? getNurseFormattedName(params.row.performedFor) : "—")}
					</Typography>
					{params.row.performedFor !== null && params.row.performedFor !== undefined && (
						<InHouseTooltip title={t("activityLog.grouped.performedFor.viewProfile")}>
							<IconButton
								size="small"
								aria-label={t("activityLog.grouped.performedFor.viewProfile")}
								component="a"
								href={`/${chosenDepartment.department_id}/nurse/${params.row.performedFor}`}
								target="_blank"
								rel="noopener noreferrer"
								onClick={(event) => event.stopPropagation()}
								data-testid={`activity-log-group-detail-performed-for-profile-${params.row.id}`}
								sx={{
									opacity: 0,
									transition: "opacity 0.1s",
									[`.${DETAIL_PANEL_ROOT_CLASS} .MuiDataGrid-row:hover &`]: { opacity: 1 },
									p: 0.25,
									flexShrink: 0,
								}}
							>
								<OpenInNewOutlinedIcon sx={{ fontSize: 14 }} />
							</IconButton>
						</InHouseTooltip>
					)}
				</Box>
			),
		},
		{
			// Field deliberately matches the sortingOptions entry's primary `sortColumns[0]`
			// ("apiRequestId") — SystemDataGrid's auto header-injection wires a customSortFunction onto a
			// column by looking up its `field` in a map keyed by each option's primary sort column (see
			// `SystemDataGrid.tsx`'s `optionByField`), so the two must agree for the header to sort at all.
			field: "apiRequestId",
			headerName: t("activityLog.grouped.detail.column.transaction"),
			flex: 0.75,
			minWidth: 140,
			renderCell: (params) => (
				<Box sx={{ display: "flex", alignItems: "center", height: "100%", gap: 0.5 }}>
					{!isOwnTransaction(params.row) && (
						<>
							<MetaChip label={transactionLabel(params.row)} tone={"info"} dataTestId={`activity-log-group-detail-transaction-${params.row.id}`} />
							{/* Hidden until this row is hovered (matches the outer grid's own `performedFor`
							view-profile action) — only a LINKED row (a different transaction pulled in via
							lineage) has anywhere to "go to"; the group's own transaction is already what's
							showing, so it gets no icon. Writes this row's own transaction id into the OUTER
							grid's search box via `onGoToTransaction`. */}
							<InHouseTooltip title={t("activityLog.grouped.detail.transaction.goToTransaction")}>
								<IconButton
									size="small"
									aria-label={t("activityLog.grouped.detail.transaction.goToTransaction")}
									onClick={(event) => {
										event.stopPropagation();
										onGoToTransaction(params.row.apiRequestId);
									}}
									data-testid={`activity-log-group-detail-go-to-transaction-${params.row.id}`}
									sx={{
										opacity: 0,
										transition: "opacity 0.1s",
										[`.${DETAIL_PANEL_ROOT_CLASS} .MuiDataGrid-row:hover &`]: { opacity: 1 },
										p: 0.25,
										flexShrink: 0,
									}}
								>
									<SearchOutlinedIcon sx={{ fontSize: 14 }} />
								</IconButton>
							</InHouseTooltip>
						</>
					)}
				</Box>
			),
		},
		{
			field: "tableName",
			headerName: t("activityLog.grouped.detail.column.table"),
			flex: 0.7,
			minWidth: 130,
			renderCell: (params) => (
				<Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
					<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY }}>{params.row.tableName}</Typography>
				</Box>
			),
		},
		{
			field: "changes",
			headerName: t("activityLog.grouped.detail.column.changes"),
			flex: 1.6,
			minWidth: 220,
			renderCell: (params) => {
				const compact = formatChangesCompact(params.row.changes);
				return (
					<InHouseTooltip title={compact}>
						<Box sx={{ display: "flex", alignItems: "center", height: "100%", minWidth: 0 }}>
							<Typography
								sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}
							>
								{compact}
							</Typography>
						</Box>
					</InHouseTooltip>
				);
			},
		},
		{
			// Mirrors the outer grid's own trailing `options` column (`GroupedActivityLog.tsx`) — same
			// structural/unsortable actions-column pattern. Own-transaction rows are already copyable from
			// the outer grid's row menu (same `apiRequestId`), so the menu here renders only for lineage
			// rows pulled in from a DIFFERENT transaction; own-transaction cells render nothing.
			field: "options",
			headerName: "",
			width: 52,
			sortable: false,
			filterable: false,
			disableColumnMenu: true,
			disableExport: true,
			excludeFromColumnPicker: true,
			renderCell: (params) =>
				isOwnTransaction(params.row) ? null : (
					<GroupedActivityLogRowMenu apiRequestId={params.row.apiRequestId} testIdPrefix="activity-log-group-detail-lineage-row-menu" />
				),
		},
	];
	// Dev Mode OFF: When/Details/Change Type/Changed By only (confirmed deliberate — see
	// `DEV_MODE_ONLY_FIELDS`'s doc comment).
	const columns = devMode ? allColumns : allColumns.filter((column) => !DEV_MODE_ONLY_FIELDS.has(column.field as keyof ActivityLogGroupRow | "options"));

	const handleCancelRemainingFetch = (): void => {
		// Disarm first, so the observer is already `enabled: false` by the time React re-renders and
		// nothing re-triggers the fetch we are about to kill.
		setFullFetchState("stopped");
		// Outlives this panel: without it, collapsing and reopening the row would restart the very fetch
		// the user is cancelling here.
		onRemainingFetchStoppedChange(entry.apiRequestId, true);
		track("activity_log_group_detail_remaining_autofetch_cancelled", {
			department_id: chosenDepartment?.department_id,
			api_request_id: entry.apiRequestId,
			shown_count: cappedQuery.data?.rows.length,
			total_count: cappedQuery.data?.totalCount,
		});
		// This aborts the REQUEST, not just the promise: the `queryFn` consumes TanStack's `signal` and
		// forwards it to axios, so `cancelQueries` reaches `AbortController.abort()` and the remaining
		// rows stop arriving. Without that thread-through, the rows would keep coming down the wire and
		// "Cancel" would cancel nothing. `exact` so the capped read (same key but `full: false`) is
		// untouched — cancelling it would blank the rows the user is looking at.
		void queryClient.cancelQueries({
			queryKey: activityLogGroupDetailQueryKey(chosenDepartment?.department_id, entry.apiRequestId, true, includePunches),
			exact: true,
		});
	};

	const handleRetryRemainingFetch = (): void => {
		track("activity_log_group_detail_remaining_retry_clicked", {
			department_id: chosenDepartment?.department_id,
			api_request_id: entry.apiRequestId,
			shown_count: cappedQuery.data?.rows.length,
			total_count: cappedQuery.data?.totalCount,
			// The two ways to reach this button: the user cancelled, or the fetch failed on its own.
			reason: fullFetchState === "stopped" ? "cancelled" : "failed",
		});
		if (fullFetchState === "stopped") {
			// Re-arming flips `enabled` false → true, which is itself what makes the observer fetch again.
			setFullFetchState("fetching");
			// Clears the persisted stop too, so the auto-fetch is armed again on the next expand — the user
			// has just said they do want these rows.
			onRemainingFetchStoppedChange(entry.apiRequestId, false);
			return;
		}
		// Already armed and already failed: writing the same state is a no-op, so nothing would happen.
		// Ask the query to run again explicitly instead.
		void fullQuery.refetch();
	};

	// While `fetching` with nothing to show yet: the in-flight indication. `isFetching` is checked
	// alongside `isError` so a RETRY of a failed fetch also shows progress — the query's status stays
	// `error` until the retry settles, which on its own would leave the button looking inert.
	const isFetchingRemaining = fullFetchState === "fetching" && !fullQuery.data && (fullQuery.isFetching || !fullQuery.isError);
	// After a cancel or a failure, and only then — `idle` deliberately shows nothing, so the one render
	// between "capped read landed truncated" and the effect arming the fetch can't flash a retry
	// prompt for a fetch that is about to start on its own.
	const canRetryRemaining = fullFetchState !== "idle" && isTruncated && !fullQuery.data && !isFetchingRemaining;
	// Search only earns its keep once a group is big enough to need it — small groups (a handful of
	// rows) are scannable at a glance, and `actionName === BULK_UPDATE_ACTION_NAME` is the backend's own
	// signal for a group past `BULK_UPDATE_THRESHOLD` (see `activityLogGroupedDisplay.ts`), so this
	// reuses that instead of duplicating the threshold as a frontend magic number.
	const isBatch = entry.actionName === BULK_UPDATE_ACTION_NAME;

	return (
		<Box
			className={DETAIL_PANEL_ROOT_CLASS}
			sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}
			data-testid={`activity-log-group-detail-${entry.apiRequestId}`}
		>
			{/* Both states reuse the same `SystemBanner` the "Showing N of M" notice already used, in the
			same slot directly above the grid — so the indication is visible without scrolling it, and
			cancel/retry land where the user's eye already is. */}
			{isFetchingRemaining && detail && (
				<SystemBanner
					tone="info"
					icon={<JumpingDots sx={{ mt: 0.5 }} />}
					title={t("activityLog.grouped.detail.loadAll.banner.loadingTitle", { shown: detail.rows.length, total: detail.totalCount })}
					action={
						<SystemButton
							buttonVariant="secondary"
							size="small"
							onClick={handleCancelRemainingFetch}
							data-testid={`activity-log-group-cancel-remaining-${entry.apiRequestId}`}
						>
							{t("activityLog.grouped.detail.loadAll.banner.cancel")}
						</SystemButton>
					}
					dataTestId={`activity-log-group-loading-remaining-banner-${entry.apiRequestId}`}
				/>
			)}
			{canRetryRemaining && detail && (
				<SystemBanner
					tone="info"
					title={t("activityLog.grouped.detail.loadAll.banner.title", { shown: detail.rows.length, total: detail.totalCount })}
					action={
						<SystemButton
							buttonVariant="secondary"
							size="small"
							onClick={handleRetryRemainingFetch}
							data-testid={`activity-log-group-load-all-${entry.apiRequestId}`}
						>
							{t("activityLog.grouped.detail.loadAll.banner.action")}
						</SystemButton>
					}
					dataTestId={`activity-log-group-load-all-banner-${entry.apiRequestId}`}
				/>
			)}
			<SystemDataGrid<ActivityLogGroupRow>
				rows={detail?.rows ?? []}
				columns={columns}
				getRowId={(row) => row.id}
				loading={isLoading}
				error={isError}
				errorMessage={t("activityLog.grouped.detail.error")}
				sortingOptions={sortingOptions}
				defaultSortState={defaultSortState}
				emptyState={
					<EmptyState
						icon={<InboxOutlinedIcon />}
						title={t("activityLog.grouped.detail.empty")}
						dataTestId={`activity-log-group-detail-empty-${entry.apiRequestId}`}
					/>
				}
				searchableColumns={
					isBatch
						? [
								"tableName",
								{ field: "changedUser", getValue: (row) => actorSearchText(rowActor(row)) },
								{ field: "objectType", getValue: (row) => objectTypeLabel(row.objectType) },
								{ field: "changeType", getValue: (row) => changeTypeText(row) },
								// `field` only needs to be a real `keyof ActivityLogGroupRow` for typing —
								// `getValue` fully overrides extraction (see `extractSearchValue`), so reusing
								// "reason" here alongside "Details"'s own composite text is fine.
								{ field: "reason", getValue: (row) => detailsText(row) },
								{ field: "changes", getValue: (row) => formatChangesCompact(row.changes) },
							]
						: undefined
				}
				searchPlaceholder={isBatch ? t("activityLog.grouped.detail.searchPlaceholder") : undefined}
				containedHeight={{ minHeight: GROUP_DETAIL_GRID_HEIGHT, maxHeight: GROUP_DETAIL_GRID_HEIGHT }}
				dataTestId={`activity-log-group-detail-grid-${entry.apiRequestId}`}
				sx={{
					"& .MuiDataGrid-columnHeader": {
						bgcolor: alpha("#FFCCBC", 0.35),
						borderBottom: "none",
					},
					"& .MuiDataGrid-columnHeaders .MuiDataGrid-filler": {
						bgcolor: alpha("#FFCCBC", 0.35),
						borderBottom: "none",
					},
					"&& .MuiDataGrid-cell": {
						paddingInline: 1,
					},
				}}
			/>
		</Box>
	);
};
